'use client';

import React, { useEffect, useLayoutEffect, useCallback, useState, useMemo, useRef, lazy, Suspense } from 'react';
import { Message as AIMessageType } from 'ai';
import { ChatHeader } from '@/components/chat-interface/chat-header';
import { ChatContextProvider } from '@/components/chat-interface/chat-context';
import { VisibilityType } from '@/components/chat-interface/visibility-selector';
import { logger } from '@/lib/logger';

// Lazy load LiveKitVoiceInterface - only load when voice mode is enabled
const LiveKitVoiceInterface = lazy(() => 
  import('@/components/chat-interface/livekit-voice-interface')
    .then(module => ({ default: module.default }))
    .catch(() => {
      // Return a component that matches the expected type signature
      const FallbackComponent = React.forwardRef<any, any>(() => (
        <div className="flex items-center justify-center h-full">
          <div className="text-sm text-neutral-500">Voice interface unavailable</div>
        </div>
      ));
      FallbackComponent.displayName = 'LiveKitVoiceInterfaceFallback';
      return { default: FallbackComponent };
    })
);

// Preload LiveKit client silently when voice is enabled
const preloadLiveKitClient = () => {
  if (typeof window === 'undefined' || window.LivekitClient) return;
  
  try {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/livekit-client/dist/livekit-client.umd.min.js';
    script.async = true;
    script.onload = () => {
      logger.debug('LiveKit client preloaded for voice mode', undefined, 'chat-ai-elements');
    };
    script.onerror = () => {
      // Silently fail - will retry when voice mode is activated
    };
    document.head.appendChild(script);
  } catch (error) {
    // Silently fail - will retry when voice mode is activated
  }
};
import { 
  getContainerClasses, 
  getGlassStyles, 
  applyWidgetStylesToDocument,
  WidgetStyle 
} from '@/lib/chat-interface/widget-styles';
import { useChatLogic, ChatLogicProps } from '@/hooks/use-chat-logic';
import {
  Conversation,
  ConversationContent,
  ConversationEmptyState,
  ConversationScrollButton,
} from '@/components/ai-elements/conversation';
import {
  Message,
  MessageContent,
  MessageActions,
  MessageAction,
  MessageResponse,
} from '@/components/ai-elements/message';
import {
  Attachments,
  Attachment,
  AttachmentPreview,
  AttachmentRemove,
} from '@/components/ai-elements/attachments';
import { Shimmer } from '@/components/ai-elements/shimmer';
import { Loader } from '@/components/ai-elements/loader';
import { CopyIcon, RefreshCcwIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { WelcomeMessage } from '@/components/chat-interface/welcome-message';
import { FloatingInputCore } from './floating-input-core';
import { useLegalConsent, LegalConsentCard, LegalDisclaimer } from './chat-legal-consent';
import {
  shouldShowAgentThinkingPhrase,
  isBareAssistantPlaceholder,
} from '@/components/chat-interface/thinking-helpers';
import { ToolLoadingSkeleton, ToolUIRenderer } from './tool-ui-renderer';
import { useSuggestiveReplies } from '@/hooks/use-suggestive-replies';
import { SuggestiveReplyChips } from '@/components/suggestive-replies/SuggestiveReplyChips';
import { MessageReactionBubble } from '@/components/chat/MessageReactionBubble';
import {
  parseMessageReactionMode,
  resolveThreadReactions,
} from '@/lib/chat/message-reaction';

export interface ChatAIElementsProps extends Omit<ChatLogicProps, 'chatbotId' | 'currentThreadId'> {
  chatbotId?: string;
  currentThreadId: string;
  welcomeMessage?: string;
  selectedChatModel?: string;
  selectedVisibilityType?: VisibilityType;
  chatbotLogoURL?: string;
  agentAvatarURL?: string;
  isReadonly?: boolean;
  chatTitle?: string;
  hideHeader?: boolean;
  isMobileMode?: boolean;
  onMobileClose?: () => void;
  displayBranding?: boolean;
  agentConfig?: {
    riveAnimationType?: string;
    riveOrbCustomColor?: string;
    riveOrbColor?: number;
  };
  widgetStyle?: WidgetStyle;
  widgetBorderStyle?: 'none' | 'simple' | 'animated';
  widgetBorderColors?: string[];
  chatbotConfig?: {
    chatVoiceEnabled?: boolean;
    chatFileAttachementEnabled?: boolean;
    branding?: boolean;
  };
  suggestedQuestions?: string[];
  accentColor?: string;
  theme?: 'light' | 'dark';
  /**
   * Embed iframes own the document. Dashboard hosts must pass false so chat
   * does not overwrite `html`/`body` classes on the app shell.
   */
  applyDocumentTheme?: boolean;
  iconType?: 'orb' | 'logo';
  onMessagesChange?: (messages: AIMessageType[]) => void;
  onNewThread?: () => void;
  /** Restore a previous conversation on mount (public pages localStorage history). */
  initialMessages?: AIMessageType[];
  /** Custom empty-thread hero (replaces the default WelcomeMessage block). */
  emptyState?: React.ReactNode;
  messageReactionMode?: string;
}

function ChatAIElementsComponent({
  chatbotId,
  currentThreadId,
  welcomeMessage,
  selectedChatModel = 'gpt-3.5-turbo',
  selectedVisibilityType = 'public',
  chatbotLogoURL,
  agentAvatarURL,
  isReadonly = false,
  chatTitle,
  hideHeader = false,
  isMobileMode = false,
  onMobileClose,
  displayBranding = false,
  agentConfig,
  widgetStyle = 'white',
  widgetBorderStyle = 'none',
  widgetBorderColors = [],
  chatbotConfig,
  suggestedQuestions,
  initialMessage,
  pendingMessage,
  onPendingMessageSent,
  initialMode = 'text',
  accentColor,
  theme = 'light',
  applyDocumentTheme = true,
  iconType,
  onMessagesChange,
  onNewThread,
  initialMessages,
  emptyState,
  messageReactionMode,
}: ChatAIElementsProps) {
  // Validate chatbotId
  if (!chatbotId) {
    logger.error('chatbotId is required but not provided', undefined, 'chat-ai-elements');
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-500">Error: Agent ID is required</p>
        </div>
      </div>
    );
  }

  // Track previous widgetStyle to avoid unnecessary DOM updates
  const prevWidgetStyleForStylesRef = useRef(widgetStyle);

  useEffect(() => {
    if (
      !applyDocumentTheme ||
      typeof document === 'undefined' ||
      !widgetStyle ||
      prevWidgetStyleForStylesRef.current === widgetStyle
    ) {
      return;
    }
    applyWidgetStylesToDocument(widgetStyle, theme);
    prevWidgetStyleForStylesRef.current = widgetStyle;
  }, [applyDocumentTheme, widgetStyle, theme]);

  // Use unified chat logic hook
  const chatLogic = useChatLogic({
    chatbotId,
    currentThreadId,
    initialMessage,
    initialMessages,
    pendingMessage,
    onPendingMessageSent,
    initialMode,
    messageReactionMode,
  });

  const reactionMode = parseMessageReactionMode(messageReactionMode);
  const latestUserMessageId = useMemo(() => {
    for (let i = chatLogic.messages.length - 1; i >= 0; i--) {
      if (chatLogic.messages[i]?.role === 'user') return chatLogic.messages[i].id;
    }
    return null;
  }, [chatLogic.messages]);
  const reactionsByUserId = useMemo(() => {
    const items = chatLogic.messages
      .filter((message) => message.role === 'user')
      .map((message) => {
        const parts = (message as { parts?: Array<{ type?: string; text?: string }> }).parts;
        const fromParts = Array.isArray(parts)
          ? parts.filter((part) => part?.type === 'text').map((part) => part.text || '').join(' ')
          : '';
        const text =
          fromParts.trim() ||
          (typeof message.content === 'string' ? message.content : '');
        return { id: message.id, text };
      });
    return resolveThreadReactions(items, reactionMode);
  }, [chatLogic.messages, reactionMode]);

  const { suggestions: suggestiveReplies, clearSuggestions } = useSuggestiveReplies({
    messages: chatLogic.messages as any,
    status: chatLogic.combinedStatus,
    surface: 'agent',
    chatbotId,
    enabled: !isReadonly && chatLogic.messages.length > 0,
  });

  // One-time legal/compliance consent (card above input, disclaimer below once accepted)
  const legalConsent = useLegalConsent(chatbotId);

  // Listen for mode change messages from drawer shell
  const handleModeChangeRef = useRef(chatLogic.handleModeChange);
  useEffect(() => {
    handleModeChangeRef.current = chatLogic.handleModeChange;
  }, [chatLogic.handleModeChange]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Use source-based validation for same-parent iframe communication
      // Messages from drawer-shell are trusted (same parent context)
      if (!event.data || typeof event.data !== 'object' || !event.data.source) {
        return;
      }
      
      // Accept messages from drawer-shell (same parent iframe context)
      // No strict origin check needed as drawer-shell and chat are in same parent context
      if (event.data.source === 'linkai-drawer-shell' || event.data.source === 'linkai-chat-page') {
        if (event.data?.type === 'MODE_CHANGE') {
          const newMode = event.data.mode;
          if (newMode === 'text' || newMode === 'voice') {
            handleModeChangeRef.current(newMode);
          }
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Notify parent window (drawer shell) when mode changes
  useEffect(() => {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({
        type: 'MODE_CHANGE',
        source: 'linkai-chat',
        mode: chatLogic.currentMode,
      }, '*');
    }
  }, [chatLogic.currentMode]);

  // Handle form submission - wrapper to match FloatingInputCore signature
  const sendCardMessage = useCallback(
    async (message: { role?: string; content?: string }) => {
      const text = message.content?.trim();
      if (!text) return;
      await chatLogic.handleSubmit(text, []);
    },
    [chatLogic.handleSubmit],
  );

  const handleFormSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const currentInput = chatLogic.input;
    const currentAttachments = [...chatLogic.attachments];
    chatLogic.setInput('');
    
    try {
      await chatLogic.handleSubmit(currentInput, currentAttachments);
    } catch (error) {
      logger.error('handleSubmit error', error instanceof Error ? { message: error.message } : error, 'chat-ai-elements');
    }
  }, [chatLogic]);

  // Handle copy message
  const handleCopyMessage = useCallback((messageId: string) => {
    const message = chatLogic.messages.find(m => m.id === messageId);
    if (message) {
      const textContent = (message as any).parts?.filter((p: any) => p.type === 'text').map((p: any) => p.text).join('') || message.content || '';
      navigator.clipboard.writeText(textContent);
    }
  }, [chatLogic.messages]);

  // Handle retry
  const handleRetry = useCallback(() => {
    if (chatLogic.reload && typeof chatLogic.reload === 'function') {
      chatLogic.reload();
    } else {
      // Fallback: resend the last user message
      const lastUserMessage = chatLogic.messages.filter(m => m.role === 'user').pop();
      if (lastUserMessage) {
        const textContent = (lastUserMessage as any).parts?.filter((p: any) => p.type === 'text').map((p: any) => p.text).join('') || lastUserMessage.content || '';
        const messageAttachments = chatLogic.messageAttachments[lastUserMessage.id] || [];
        if (textContent || messageAttachments.length > 0) {
          chatLogic.handleSubmit(textContent, messageAttachments);
        }
      }
    }
  }, [chatLogic]);

  // Preload voice mode components silently when voice is enabled
  useEffect(() => {
    if (chatbotConfig?.chatVoiceEnabled) {
      preloadLiveKitClient();
    }
  }, [chatbotConfig?.chatVoiceEnabled]);

  // Track previous theme to avoid unnecessary DOM updates
  const prevThemeRef = useRef(theme);
  const prevWidgetStyleRef = useRef(widgetStyle);

  useLayoutEffect(() => {
    if (!applyDocumentTheme || typeof document === 'undefined') return;

    const themeChanged = prevThemeRef.current !== theme;
    const widgetStyleChanged = prevWidgetStyleRef.current !== widgetStyle;

    if (themeChanged || widgetStyleChanged) {
      if (widgetStyle === 'glass') {
        document.documentElement.className = 'glass-theme';
        document.body.style.backgroundColor = 'transparent';
        document.documentElement.style.backgroundColor = 'transparent';
      } else {
        document.documentElement.className = theme || 'light';
        document.body.style.backgroundColor = '';
        document.documentElement.style.backgroundColor = '';
      }

      if (widgetStyle !== 'glass') {
        document.documentElement.style.colorScheme = (theme || 'light') === 'dark' ? 'dark' : 'light';
      }

      prevThemeRef.current = theme;
      prevWidgetStyleRef.current = widgetStyle;
    }
  }, [applyDocumentTheme, theme, widgetStyle]);

  // Memoize container styling to prevent unnecessary re-renders
  const containerClasses = useMemo(
    () => getContainerClasses(widgetStyle, theme || 'light'),
    [widgetStyle, theme]
  );
  
  const glassStyles = useMemo(
    () => getGlassStyles(widgetStyle, widgetBorderStyle, theme || 'light'),
    [widgetStyle, widgetBorderStyle, theme]
  );
  
  const isDark = theme === 'dark';

  const thinkingPhrase = chatTitle ? `${chatTitle} is thinking...` : 'Thinking...';

  const hasAttachments = useMemo(() => {
    return chatLogic.attachments.length > 0;
  }, [chatLogic.attachments]);

  // Notify parent when messages change
  useEffect(() => {
    if (onMessagesChange) {
      onMessagesChange(chatLogic.messages);
    }
  }, [chatLogic.messages, onMessagesChange]);

  return (
    <ChatContextProvider append={sendCardMessage} accentColor={accentColor}>
    <div 
      className={cn(
        'flex flex-col relative',
        isMobileMode ? 'h-full' : 'h-screen',
        containerClasses,
        'widget-transition iframe-glass-fix custom-scrollbar',
        widgetStyle === 'glass' && 'glass-effect',
        (theme || 'light') === 'dark' && 'dark'
      )} 
      style={glassStyles}
      id={`chat-${currentThreadId}`}
      data-theme={theme || 'light'}
    >
      {!hideHeader && (
        <ChatHeader
          chatId={currentThreadId}
          selectedModelId={selectedChatModel}
          selectedVisibilityType={selectedVisibilityType}
          isReadonly={isReadonly}
          chatTitle={chatTitle}
          currentMode={chatLogic.currentMode}
          onModeChange={chatLogic.handleModeChange}
          isThinking={chatLogic.messages.length > 0 && (chatLogic.combinedStatus === 'submitted' || chatLogic.combinedStatus === 'streaming')}
          isWelcomeMode={chatLogic.messages.length === 0}
          isMobileMode={isMobileMode}
          onMobileClose={onMobileClose}
          widgetStyle={widgetStyle}
          chatVoiceEnabled={chatbotConfig?.chatVoiceEnabled ?? false}
          iconType={iconType}
          agentAvatarURL={agentAvatarURL}
          agentConfig={agentConfig}
          onNewThread={onNewThread}
        />
      )}

      {chatLogic.currentMode === 'text' ? (
        <div className={cn(
          "flex flex-col h-full min-h-0",
          widgetStyle === 'glass' ? 'bg-transparent' : 'bg-background'
        )}>
          <Conversation className="flex-1 min-h-0">
            {/* Extra bottom padding keeps the last message/thinking indicator above
                the input's gradient fade (which is only meant for scroll fade) */}
            <ConversationContent className="!pb-12">
              {chatLogic.messages.length === 0 &&
                (emptyState ? (
                  emptyState
                ) : (
                  <div className="flex flex-1 flex-col justify-center w-full min-h-[50vh] pb-24">
                    <WelcomeMessage
                      message={welcomeMessage}
                      theme={widgetStyle === 'black' ? 'dark' : (theme || 'light') as 'light' | 'dark'}
                    />
                  </div>
                ))}
              {(() => {
                const showThinkingPhrase = shouldShowAgentThinkingPhrase({
                  status: chatLogic.combinedStatus,
                  messages: chatLogic.messages,
                });
                return chatLogic.messages.map((message) => {
                const isUser = message.role === 'user';
                const parts = (message as any).parts || [];
                const isLastMessage = message === chatLogic.messages[chatLogic.messages.length - 1];
                const isStreaming = chatLogic.combinedStatus === 'streaming' && 
                                  message.role === 'assistant' && 
                                  isLastMessage;

                // Get file attachments for this message
                const messageAttachments = chatLogic.messageAttachments[message.id] || [];

                if (
                  showThinkingPhrase &&
                  message.role === 'assistant' &&
                  isLastMessage &&
                  isBareAssistantPlaceholder(message as any)
                ) {
                  return <React.Fragment key={message.id} />;
                }

                return (
                  <Message
                    key={message.id}
                    from={isUser ? 'user' : 'assistant'}
                  >
                    {/* Show attachments outside the message bubble, aligned with message */}
                    {messageAttachments.length > 0 && (
                      <div className={cn(
                        "flex w-fit max-w-full flex-wrap gap-2 mb-2",
                        isUser ? 'ml-auto justify-end' : 'justify-start'
                      )}>
                        <Attachments
                          variant="grid"
                          className={cn(
                            "[&>div]:aspect-square [&>div]:w-32",
                            isUser ? "ml-auto" : "ml-0"
                          )}
                        >
                          {messageAttachments.map((attachment: any) => (
                            <Attachment
                              key={attachment.id || attachment.url}
                              data={{
                                id: attachment.id || attachment.fileId || attachment.url || '',
                                type: 'file' as const,
                                url: attachment.url || '',
                                filename: attachment.name || attachment.filename || 'Attachment',
                                mimeType: attachment.contentType || attachment.mimeType,
                              } as any}
                              className="w-32 aspect-square"
                            >
                              <AttachmentPreview className="w-full h-full" />
                              <AttachmentRemove />
                            </Attachment>
                          ))}
                        </Attachments>
                      </div>
                    )}
                    
                    {(() => {
                      const reaction = isUser ? reactionsByUserId.get(message.id) ?? null : null;
                      const animateReaction =
                        isUser &&
                        message.id === latestUserMessageId &&
                        (chatLogic.combinedStatus === 'submitted' ||
                          chatLogic.combinedStatus === 'streaming');
                      return (
                    <div className={cn(isUser && 'relative w-fit max-w-full', isUser && reaction && 'mb-2')}>
                    <MessageContent>
                      {/* Show text content */}
                      {parts.length > 0 ? (
                        parts.map((part: any, partIndex: number) => {
                          if (part.type === 'text') {
                            return (
                              <MessageResponse key={`${message.id}-${partIndex}`}>
                                {part.text}
                              </MessageResponse>
                            );
                          }
                          return null;
                        })
                      ) : message.content ? (
                        <MessageResponse>
                          {message.content}
                        </MessageResponse>
                      ) : null}
                    </MessageContent>
                    {isUser && reaction ? (
                      <MessageReactionBubble
                        emoji={reaction}
                        seed={message.id}
                        animate={animateReaction}
                        text={
                          typeof message.content === 'string'
                            ? message.content
                            : parts
                                .filter((part: { type?: string; text?: string }) => part.type === 'text')
                                .map((part: { text?: string }) => part.text ?? '')
                                .join(' ')
                        }
                      />
                    ) : null}
                    </div>
                      );
                    })()}

                    {/* Generative UI for tool invocations */}
                    {parts.length > 0 && parts.some((p: any) => p.type?.startsWith('tool-')) && (
                      <div className="flex flex-col gap-2 w-full max-w-[calc(100%-2rem)]">
                        {(() => {
                          // Deduplicate: for each tool name, prefer the last successful
                          // output-available part. While a tool is still running, show its
                          // loading card so the turn never goes blank after a lead-in ack.
                          const RUNNING_TOOL_STATES = new Set([
                            'input-available',
                            'input-streaming',
                            'call',
                            'partial-call',
                          ]);
                          const toolParts = parts
                            .map((part: any, partIndex: number) => ({ part, partIndex }))
                            .filter(
                              ({ part }: any) =>
                                part.type?.startsWith('tool-') &&
                                (part.state === 'output-available' ||
                                  RUNNING_TOOL_STATES.has(part.state)),
                            );

                          const seen = new Map<string, { part: any; partIndex: number }>();
                          for (const entry of toolParts) {
                            const toolName = entry.part.type.replace('tool-', '');
                            const prev = seen.get(toolName);
                            const isDone = entry.part.state === 'output-available';
                            const prevIsDone = prev?.part.state === 'output-available';
                            const isSuccess = entry.part.output?.success === true;
                            const prevIsSuccess = prev?.part.output?.success === true;
                            if (!prev) {
                              seen.set(toolName, entry);
                              continue;
                            }
                            // Prefer completed results over in-flight; among completed, prefer success.
                            if (isDone && !prevIsDone) {
                              seen.set(toolName, entry);
                            } else if (isDone && prevIsDone && (isSuccess || !prevIsSuccess)) {
                              seen.set(toolName, entry);
                            } else if (!isDone && !prevIsDone) {
                              seen.set(toolName, entry);
                            }
                          }

                          return Array.from(seen.values()).map(({ part, partIndex }) => {
                            const toolName = part.type.replace('tool-', '');
                            if (part.state !== 'output-available') {
                              return (
                                <ToolLoadingSkeleton
                                  key={`${message.id}-tool-loading-${partIndex}`}
                                  toolName={toolName}
                                  args={part.input || part.args}
                                />
                              );
                            }
                            return (
                              <ToolUIRenderer
                                key={`${message.id}-tool-${partIndex}`}
                                toolName={toolName}
                                result={part.output || {}}
                                args={part.input}
                                chatbotId={chatbotId}
                                threadId={currentThreadId}
                              />
                            );
                          });
                        })()}
                      </div>
                    )}

                    {/* Legacy toolInvocations format */}
                    {(!parts || parts.length === 0) && (message as any).toolInvocations?.length > 0 && (
                      <div className="flex flex-col gap-2 w-full max-w-[calc(100%-2rem)]">
                        {(message as any).toolInvocations
                          .filter((inv: any) => inv.state === 'result')
                          .map((inv: any) => (
                            <ToolUIRenderer
                              key={inv.toolCallId}
                              toolName={inv.toolName}
                              result={inv.result}
                              args={inv.args}
                              chatbotId={chatbotId}
                              threadId={currentThreadId}
                            />
                          ))}
                      </div>
                    )}
                    
                    {/* Show actions for assistant messages */}
                    {!isUser && isLastMessage && (
                      <MessageActions>
                        <MessageAction
                          onClick={handleRetry}
                          label="Retry"
                          tooltip="Retry"
                        >
                          <RefreshCcwIcon className="size-3" />
                        </MessageAction>
                        <MessageAction
                          onClick={() => handleCopyMessage(message.id)}
                          label="Copy"
                          tooltip="Copy"
                        >
                          <CopyIcon className="size-3" />
                        </MessageAction>
                      </MessageActions>
                    )}
                  </Message>
                );
              });
              })()}
              
              {/* Thinking until first assistant text (submitted or streaming with empty assistant row) */}
              {shouldShowAgentThinkingPhrase({
                status: chatLogic.combinedStatus,
                messages: chatLogic.messages,
              }) && (
                <Message from="assistant" key="thinking-message">
                  <MessageContent>
                    <Shimmer duration={3} spread={4} className="text-sm">
                      {thinkingPhrase}
                    </Shimmer>
                  </MessageContent>
                </Message>
              )}
            </ConversationContent>
            <ConversationScrollButton />
          </Conversation>

          {/* Input Section - Using same FloatingInputCore component - Always visible */}
          <div 
            className="flex-shrink-0 pt-0 pb-4 px-4 relative"
            style={{
              background: 'transparent',
              backgroundColor: 'transparent',
              position: 'relative',
              zIndex: 10,
            }}
          >
            {/* Gradient fade overlay - positioned above input section (like legacy window) */}
            {widgetStyle !== 'glass' && (
              <div 
                className="absolute bottom-full left-0 right-0 h-10 pointer-events-none"
                style={{
                  zIndex: 5,
                  background:
                    'linear-gradient(to top, hsl(var(--background)) 0%, hsl(var(--background) / 0.82) 45%, transparent 100%)',
                }}
              />
            )}
            
            {chatLogic.messages.length === 0 && suggestedQuestions && suggestedQuestions.length > 0 && (
              <div className="mb-2">
                <SuggestiveReplyChips
                  suggestions={suggestedQuestions.slice(0, 3)}
                  variant="widget"
                  widgetStyle={widgetStyle}
                  theme={(theme || 'light') as 'light' | 'dark'}
                  layout="stack"
                  onSelect={(question) => {
                    chatLogic.handleSubmit(question, []);
                  }}
                />
              </div>
            )}

            {suggestiveReplies.length > 0 && (
              <div className="mb-2">
                <SuggestiveReplyChips
                  suggestions={suggestiveReplies}
                  variant="widget"
                  widgetStyle={widgetStyle}
                  theme={(theme || 'light') as 'light' | 'dark'}
                  onSelect={(suggestion) => {
                    clearSuggestions();
                    void chatLogic.handleSubmit(suggestion, []);
                  }}
                />
              </div>
            )}
            
            {/* One-time legal notice above the input; collapses on accept */}
            <LegalConsentCard
              mounted={legalConsent.mounted}
              accepted={legalConsent.accepted}
              onAccept={legalConsent.accept}
              widgetStyle={widgetStyle}
              theme={(theme || 'light') as 'light' | 'dark'}
              accentColor={accentColor}
            />

            <FloatingInputCore
              input={chatLogic.input}
              setInput={chatLogic.setInput}
              onSubmit={handleFormSubmit}
              isLoading={chatLogic.isLoading}
              widgetStyle={widgetStyle}
              accentColor={accentColor}
              chatVoiceEnabled={chatbotConfig?.chatVoiceEnabled || false}
              chatFileAttachementEnabled={chatbotConfig?.chatFileAttachementEnabled || false}
              chatbotId={chatbotId}
              threadId={currentThreadId}
              theme={theme}
              placeholder="Ask a question..."
              onVoiceClick={() => chatLogic.handleModeChange('voice')}
              drawerMode={true}
              disabled={chatLogic.isLoading}
              attachments={chatLogic.attachments}
              onAttachmentRemove={chatLogic.handleAttachmentRemove}
              onAttachmentAdd={chatLogic.handleAttachmentAdd}
            />

            <LegalDisclaimer
              mounted={legalConsent.mounted}
              accepted={legalConsent.accepted}
              widgetStyle={widgetStyle}
              theme={(theme || 'light') as 'light' | 'dark'}
            />
          </div>
        </div>
      ) : (
        <Suspense fallback={
          <div className={cn(
            "flex items-center justify-center h-full",
            // Theme-aware background: only apply when widgetStyle is 'white' (not glass or black)
            widgetStyle === 'white' 
              ? (theme === 'dark' ? 'bg-neutral-900' : 'bg-white')
              : widgetStyle === 'black'
              ? 'bg-black'
              : 'bg-transparent' // glass style uses transparent
          )}>
            <div className={cn(
              "text-sm",
              widgetStyle === 'black' 
                ? 'text-white' 
                : widgetStyle === 'glass' 
                  ? 'text-white' 
                  : theme === 'dark'
                    ? 'text-neutral-400'
                    : 'text-neutral-500'
            )}>
              Loading voice interface...
            </div>
          </div>
        }>
          <LiveKitVoiceInterface
            chatbotId={chatbotId}
            welcomeMessage={welcomeMessage}
            onTranscriptReceived={chatLogic.handleTranscriptReceived}
            onAgentResponseReceived={chatLogic.handleAgentResponseReceived}
            debug={false}
            agentConfig={agentConfig}
            widgetStyle={widgetStyle}
            widgetBorderStyle={widgetBorderStyle}
            widgetBorderColors={widgetBorderColors}
            theme={theme}
            accentColor={accentColor}
            ref={chatLogic.voiceInterfaceRef}
          />
        </Suspense>
      )}
    </div>
    </ChatContextProvider>
  );
}

// Memoize ChatAIElements to prevent unnecessary re-renders
export const ChatAIElements = React.memo(ChatAIElementsComponent, (prevProps, nextProps) => {
  // Custom comparison: only re-render if these props change
  return (
    prevProps.chatbotId === nextProps.chatbotId &&
    prevProps.currentThreadId === nextProps.currentThreadId &&
    prevProps.chatTitle === nextProps.chatTitle &&
    prevProps.widgetStyle === nextProps.widgetStyle &&
    prevProps.theme === nextProps.theme &&
    prevProps.displayBranding === nextProps.displayBranding &&
    prevProps.hideHeader === nextProps.hideHeader &&
    prevProps.isMobileMode === nextProps.isMobileMode &&
    JSON.stringify(prevProps.agentConfig) === JSON.stringify(nextProps.agentConfig) &&
    JSON.stringify(prevProps.chatbotConfig) === JSON.stringify(nextProps.chatbotConfig) &&
    JSON.stringify(prevProps.suggestedQuestions) === JSON.stringify(nextProps.suggestedQuestions) &&
    prevProps.initialMode === nextProps.initialMode &&
    prevProps.pendingMessage === nextProps.pendingMessage &&
    prevProps.emptyState === nextProps.emptyState
  );
});
